import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import MapView from "../components/MapView";
import PlaceCard from "../components/PlaceCard";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Search, X, ChevronDown, ChevronUp } from "lucide-react";
import CategoryIcon from "../components/CategoryIcon";
import { CATEGORIES, CATEGORY_ORDER, PRIORITY_CITIES, OTHER_CITIES } from "../lib/categories";

export default function GlobalMapPage() {
  const [params, setParams] = useSearchParams();
  const [allPlaces, setAllPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [highlightedId, setHighlightedId] = useState(null);
  const [showList, setShowList] = useState(false);

  const q = params.get("q") || "";
  const city = params.get("city") || "";
  const activeCategories = useMemo(() => {
    const raw = params.get("cats");
    if (!raw) return new Set(CATEGORY_ORDER);
    return new Set(raw.split(",").filter(Boolean));
  }, [params]);

  useEffect(() => {
    setLoading(true);
    const query = {};
    if (q) query.q = q;
    if (city) query.city = city;
    api
      .get("/places", { params: query })
      .then(({ data }) => setAllPlaces(data))
      .catch(() => setAllPlaces([]))
      .finally(() => setLoading(false));
  }, [q, city]);

  const places = useMemo(
    () => allPlaces.filter((p) => activeCategories.has(p.category)),
    [allPlaces, activeCategories]
  );

  const updateParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (!value || value === "all") next.delete(key);
    else next.set(key, value);
    setParams(next);
  };

  const toggleCategory = (key) => {
    const next = new Set(activeCategories);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    const params2 = new URLSearchParams(params);
    if (next.size === CATEGORY_ORDER.length) params2.delete("cats");
    else params2.set("cats", Array.from(next).join(","));
    setParams(params2);
  };

  const selectAllCategories = () => {
    const params2 = new URLSearchParams(params);
    params2.delete("cats");
    setParams(params2);
  };

  const clearAll = () => setParams(new URLSearchParams());

  const totalCount = places.length;

  return (
    <div className="max-w-[1600px] mx-auto px-3 sm:px-5 py-4">
      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
        {/* Sidebar filters */}
        <aside className="bg-white border border-brand-line rounded-2xl p-5 lg:sticky lg:top-20 lg:max-h-[calc(100vh-100px)] lg:overflow-y-auto shadow-[0_8px_32px_rgba(45,42,38,0.04)]">
          <div className="mb-4">
            <div className="text-xs uppercase tracking-[0.2em] text-brand-terracotta font-semibold mb-1">
              Carte du Tchad
            </div>
            <h1 className="font-heading text-2xl font-semibold text-brand-ink tracking-tight">
              Explorer
            </h1>
            <p className="text-sm text-brand-inkSoft mt-1">
              {loading ? "Chargement..." : `${totalCount} annonces visibles`}
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-brand-ink uppercase tracking-wider mb-2 block">
                Recherche
              </label>
              <div className="flex items-center gap-2 px-3 bg-brand-sandlight rounded-xl">
                <Search className="w-4 h-4 text-brand-inkMuted" />
                <Input
                  value={q}
                  onChange={(e) => updateParam("q", e.target.value)}
                  placeholder="Nom, marque, ville..."
                  className="border-0 bg-transparent shadow-none focus-visible:ring-0 h-10"
                  data-testid="global-search-input"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-brand-ink uppercase tracking-wider mb-2 block">
                Ville
              </label>
              <Select value={city || "all"} onValueChange={(v) => updateParam("city", v)}>
                <SelectTrigger className="h-11 rounded-xl border-brand-line" data-testid="global-city-select">
                  <SelectValue placeholder="Toutes les villes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les villes</SelectItem>
                  <div className="px-2 pt-1 pb-0.5 text-[10px] uppercase tracking-wider text-brand-terracotta font-bold">
                    Prioritaires
                  </div>
                  {PRIORITY_CITIES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                  <div className="px-2 pt-2 pb-0.5 text-[10px] uppercase tracking-wider text-brand-inkMuted font-bold">
                    Autres
                  </div>
                  {OTHER_CITIES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold text-brand-ink uppercase tracking-wider">
                  Catégories
                </label>
                <button
                  type="button"
                  onClick={selectAllCategories}
                  className="text-xs text-brand-terracotta hover:underline font-medium"
                  data-testid="select-all-cats"
                >
                  Tout sélectionner
                </button>
              </div>
              <div className="space-y-1">
                {CATEGORY_ORDER.map((key) => {
                  const cat = CATEGORIES[key];
                  const active = activeCategories.has(key);
                  return (
                    <label
                      key={key}
                      className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-brand-sandlight cursor-pointer transition-colors"
                      data-testid={`cat-checkbox-${key}`}
                    >
                      <Checkbox
                        checked={active}
                        onCheckedChange={() => toggleCategory(key)}
                        className="border-brand-line"
                      />
                      <div
                        className="w-6 h-6 rounded-md flex items-center justify-center text-white shrink-0"
                        style={{ backgroundColor: cat.color }}
                      >
                        <CategoryIcon name={cat.icon} className="w-3.5 h-3.5" />
                      </div>
                      <span className="text-sm text-brand-ink flex-1">{cat.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {(q || city || activeCategories.size < CATEGORY_ORDER.length) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAll}
                className="w-full text-brand-terracotta hover:bg-brand-sandlight rounded-full"
                data-testid="clear-global-filters"
              >
                <X className="w-4 h-4 mr-1" /> Effacer les filtres
              </Button>
            )}
          </div>
        </aside>

        {/* Map + collapsible list */}
        <div>
          <div className="h-[60vh] sm:h-[70vh] lg:h-[calc(100vh-100px)]">
            <MapView places={places} highlightedId={highlightedId} />
          </div>

          {/* Toggleable list */}
          <div className="mt-4">
            <button
              type="button"
              onClick={() => setShowList((s) => !s)}
              className="w-full flex items-center justify-between p-4 rounded-2xl bg-white border border-brand-line hover:bg-brand-sandlight transition-colors"
              data-testid="toggle-global-list"
            >
              <span className="font-heading font-semibold text-brand-ink">
                {showList ? "Masquer la liste" : `Voir la liste (${totalCount})`}
              </span>
              {showList ? (
                <ChevronUp className="w-5 h-5 text-brand-inkSoft" />
              ) : (
                <ChevronDown className="w-5 h-5 text-brand-inkSoft" />
              )}
            </button>
            {showList && (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 mt-5">
                {places.map((p, i) => (
                  <div key={p.id} className="fade-up" style={{ animationDelay: `${i * 30}ms` }}>
                    <PlaceCard
                      place={p}
                      onHover={setHighlightedId}
                      onLeave={() => setHighlightedId(null)}
                    />
                  </div>
                ))}
                {places.length === 0 && !loading && (
                  <div className="col-span-full bg-white border border-brand-line rounded-2xl p-10 text-center text-brand-inkSoft">
                    Aucune annonce ne correspond à vos filtres.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
